import { planSettlement } from '../src/lib/settlement.ts'

export interface SqlClient {
  query<Row extends Record<string, unknown> = Record<string, unknown>>(
    sql: string,
    params: unknown[],
  ): Promise<{ rows: Row[] }>
}

export interface RecordPaymentInput {
  userId: string
  amountCents: number
  paymentMethod?: 'stripe' | 'bank_transfer'
  stripeCheckoutId?: string | null
  confirmedAt?: Date | null
  createdAt?: Date
  settledAt?: Date
  /** Provide to make the payment id deterministic; otherwise generated. */
  paymentId?: string
  /** Provide to make an overpayment credit deterministic. */
  creditId?: string
}

/**
 * Settlement engine — a faithful port of drink-counter's `recordPayment`, the
 * single chokepoint every payment channel funnels through. Runs inside a
 * caller-provided transaction so settlement stays atomic.
 *
 *   1. Create the payment row.
 *   2. Pay FEES oldest-first, whole-item, STOP at the first fee too big.
 *   3. Pay DRINKS oldest-first, whole-item, SKIP any single drink too expensive.
 *   4. Any remainder becomes a Credit linked to the payment.
 *
 * Never partially pays an item. Returns the payment id.
 */
export async function recordPayment(tx: SqlClient, input: RecordPaymentInput): Promise<string> {
  const paymentId = input.paymentId ?? crypto.randomUUID()
  const settledAt = input.settledAt ?? new Date()

  await tx.query(
    `insert into payments (
       payment_id, user_id, amount_cents, stripe_checkout_id,
       payment_method, created_at, confirmed_at
     )
     values ($1, $2, $3, $4, $5, $6, $7)`,
    [
      paymentId,
      input.userId,
      input.amountCents,
      input.stripeCheckoutId ?? null,
      input.paymentMethod ?? 'stripe',
      input.createdAt ?? new Date(),
      input.confirmedAt ?? null,
    ],
  )

  const fees = await tx.query<{ id: string; amount_cents: number }>(
    `select id, amount_cents from fees
      where user_id = $1 and deleted_at is null and paid_at is null
      order by fee_date asc, created_at asc`,
    [input.userId],
  )

  const drinks = await tx.query<{ id: string; price_cents: number }>(
    `select ud.id, d.price_cents
       from user_drinks ud
       join drinks d on d.drink_id = ud.drink_id
      where ud.user_id = $1 and ud.paid_at is null
      order by ud.purchased_at asc`,
    [input.userId],
  )

  const plan = planSettlement(
    input.amountCents,
    fees.rows.map((fee) => ({ id: fee.id, amountCents: fee.amount_cents })),
    drinks.rows.map((drink) => ({ id: drink.id, amountCents: drink.price_cents })),
  )

  for (const feeId of plan.feeIds) {
    await tx.query(
      `update fees set paid_at = $3, payment_id = $2
        where id = $1 and paid_at is null`,
      [feeId, paymentId, settledAt],
    )
  }
  for (const drinkId of plan.drinkIds) {
    await tx.query(
      `update user_drinks set paid_at = $3, payment_id = $2
        where id = $1 and paid_at is null`,
      [drinkId, paymentId, settledAt],
    )
  }

  if (plan.remainderCents > 0) {
    await tx.query(
      `insert into credits (
         id, user_id, payment_id, amount_cents, remaining_amount_cents,
         description, created_at
       )
       values ($1, $2, $3, $4, $4, $5, $6)`,
      [
        input.creditId ?? crypto.randomUUID(),
        input.userId,
        paymentId,
        plan.remainderCents,
        'Overpayment credit',
        settledAt,
      ],
    )
  }

  return paymentId
}
